export const DEFAULT_MAX_SCORE = 10;

export type ScoreDisplayStatus = 'pass' | 'partial' | 'fail';

export interface ScoreDisplayConfig {
  labelKey: 'fullyReady' | 'partiallyReady' | 'notReady';
  color: 'green' | 'orange' | 'red';
}

export const SCORE_DISPLAY_CONFIG: Record<
  ScoreDisplayStatus,
  ScoreDisplayConfig
> = {
  pass: {
    labelKey: 'fullyReady',
    color: 'green',
  },
  partial: {
    labelKey: 'partiallyReady',
    color: 'orange',
  },
  fail: {
    labelKey: 'notReady',
    color: 'red',
  },
};

export const hasValidMaxScore = (
  maxScore: number | null | undefined
): maxScore is number =>
  typeof maxScore === 'number' && Number.isFinite(maxScore) && maxScore > 0;

export const resolveMaxScore = (
  maxScore: number | null | undefined,
  fallbackMaxScore: number = DEFAULT_MAX_SCORE
): number => (hasValidMaxScore(maxScore) ? maxScore : fallbackMaxScore);

export const isMaxScoreReached = (
  score: number | null | undefined,
  maxScore: number | null | undefined,
  fallbackMaxScore: number = DEFAULT_MAX_SCORE
): boolean => {
  if (typeof score !== 'number' || !Number.isFinite(score)) {
    return false;
  }

  return score >= resolveMaxScore(maxScore, fallbackMaxScore);
};

export const getScoreDisplayStatus = (
  score: number | null | undefined,
  maxScore: number | null | undefined,
  fallbackMaxScore: number = DEFAULT_MAX_SCORE
): ScoreDisplayStatus => {
  if (typeof score !== 'number' || !Number.isFinite(score) || score <= 0) {
    return 'fail';
  }
  if (isMaxScoreReached(score, maxScore, fallbackMaxScore)) {
    return 'pass';
  }
  return 'partial';
};

export const getScoreDisplayConfig = (
  score: number | null | undefined,
  maxScore: number | null | undefined,
  fallbackMaxScore: number = DEFAULT_MAX_SCORE
): ScoreDisplayConfig =>
  SCORE_DISPLAY_CONFIG[
    getScoreDisplayStatus(score, maxScore, fallbackMaxScore)
  ];
